import { CommonModule, NgIf } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Component,
  Input,
  OnChanges,
  Provider,
  SimpleChanges,
  computed,
  forwardRef,
  input,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  FormControl,
} from '@angular/forms';
import { FileUrlPipe } from './pipes/file-url.pipe';
import { MatDialog } from '@angular/material/dialog';
import { MultipleImageDialogComponent } from '../../multiple-image-dialog/image-dialog.component';
import { ValidationMessageFn } from '../types/validation-message-fn';
import { SharedModule } from 'src/app/shared.module';
import i18next from 'i18next';

const REACTIVE_FILE_INPUT_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ReactiveFileInputBusinessDocumentsComponent),
  multi: true,
};
@Component({
  selector: 'app-reactive-file-input',
  templateUrl: './reactive-file-input.component.html',
  styleUrls: ['./reactive-file-input.component.scss'],
  providers: [REACTIVE_FILE_INPUT_ACCESSOR],
  standalone: true,
  imports: [CommonModule, FileUrlPipe, SharedModule],
})
export class ReactiveFileInputBusinessDocumentsComponent
  implements ControlValueAccessor, OnChanges
{
  constructor(private dialog: MatDialog) {}
  ngOnChanges(changes: SimpleChanges): void {
    console.log("onchange reactive file input", changes)
    if("sampleImageUrl" in changes){
      console.log("sampleImageChange")
      this.onFileRemoved()
    }
  }

  @Input()
  public labelText: string = 'File Input';

  @Input()
  public placeholder: string = i18next.t(
    'advanced-pages.documents.upload_file'
  );

  @Input() 
  public formControl: FormControl = new FormControl();

  @Input()
  public id: string = 'file-input';

  @Input()
  public sampleImageUrl: string[] = [];

  @Input()
  public acceptedFileTypes: Array<String> = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'application/pdf',
  ];

  @Input()
  public errors: ValidationErrors | null | undefined = null;

  @Input()
  public validationMessage: Record<string, ValidationMessageFn> = {};

  @Input()
  public documentURL: string | null = null;

  @Input()
  public fromTh: boolean = true;

  public file = signal<File | null>(null);
  public oneOfTheFollowing(): boolean {
    return this.labelText != 'At least one of the following:';
  }
  public hasFile = computed(() => this.file() !== null);
  public isFileImage = computed(() => {
    const file = this.file();
    return file ? file.type.includes('image') : false;
  });

  public get hasErrors(): boolean {
    return this.errors !== null;
  }

  /**
   * This function is need to generate the accept string for the file input
   * in order to filter the file types that are allowed to be uploaded.
   * @returns
   */
  public buildAcceptString(): string {
    return this.acceptedFileTypes.join(',');
  }

  public onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;

    this.file.set(file);
    this.onChange(file);
    this.onTouched();
    input.value = ''
  }

  public onFileRemoved(): void {
    this.file.set(null);
    this.onChange(null);
    this.onTouched();
  }

  public get errorMessage(): string {
    const firstError = Object.keys(this.formControl.errors || {})[0];
    const messageFn = this.validationMessage[firstError];
    return messageFn ? messageFn(this.formControl.errors) : '';
  }

  openImageDialog(imageUrl: string[]): void {
    console.log('OPENNNN', imageUrl);
    this.dialog.open(MultipleImageDialogComponent, {
      data: { imageUrl },
      maxWidth: '80vw',
      maxHeight: '80vh',
    });
  }

  private onChange: (value: File | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(obj: File): void {
    this.file.set(obj);
  }

  registerOnChange(fn: typeof this.onChange): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: typeof this.onTouched): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  formattedLabelText(): string {
    if (this.labelText) {
      let formattedText = this.labelText;

      // Replace underscores with spaces and capitalize each word
      formattedText = formattedText
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      // Replace 'Id' with 'ID'
      formattedText = formattedText.replace(/\bId\b/g, 'ID');

      return formattedText;
    }

    return '';
  }
}
